/*
 * Copyright (C) 2026 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { ButtonComponent } from '@app/app/shared/styleguide/components/buttons/default/button.component';
import { IconComponent } from '@app/app/shared/styleguide/components/icon/icon.component';
import { marker } from '@jsverse/transloco-keys-manager/marker';
import { LucideCookie, LucideExternalLink } from '@lucide/angular';
import { TranslocoModule } from '@ngneat/transloco';
import { CookieConsentService } from '../../services/cookie-consent/cookie-consent.service';

/**
 * Stands in for a third-party embed (Iframely preview, video player, map) while the visitor has not allowed
 * embedded content in the cookie settings. Offers the settings panel and a plain link to the source instead.
 */
@Component({
  selector: 'ptl-embed-consent-placeholder',
  standalone: true,
  imports: [TranslocoModule, IconComponent, ButtonComponent],
  templateUrl: './embed-consent-placeholder.component.html',
  styleUrls: ['./embed-consent-placeholder.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmbedConsentPlaceholderComponent {
  readonly url = input<string | null | undefined>(null);
  readonly siteName = input<string | null | undefined>(null);
  readonly compact = input(false);

  private readonly cookieConsent = inject(CookieConsentService);

  readonly LucideCookie = LucideCookie;
  readonly LucideExternalLink = LucideExternalLink;

  readonly titleKey = marker('translations.cookieConsent.embedPlaceholder.title');
  readonly textKey = marker('translations.cookieConsent.embedPlaceholder.text');
  readonly settingsKey = marker('translations.cookieConsent.embedPlaceholder.settings');
  readonly openKey = marker('translations.cookieConsent.embedPlaceholder.open');
  readonly unknownSiteKey = marker('translations.cookieConsent.embedPlaceholder.unknownSite');

  readonly siteLabel = computed<string | null>(() => this.siteName()?.trim() || hostOf(this.url()));

  openSettings(): void {
    this.cookieConsent.open();
  }
}

function hostOf(url: string | null | undefined): string | null {
  if (!url) {
    return null;
  }
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return null;
  }
}
